import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../css/SearchBar.css';
import { BACKEND_URL } from '../config';

// ============================================================================
// КОНСТАНТИ ДЛЯ "ЗАХИСТУ ВІД МАВП"
// ============================================================================

const DEBOUNCE_DELAY = 400; // мс - затримка перед запитом
const MIN_QUERY_LENGTH = 3; // мінімальна довжина для пошуку
const CACHE_EXPIRATION = 5 * 60 * 1000; // 5 хвилин - час життя кешу
const SUGGESTIONS_LIMIT = 10; // максимальна кількість підказок

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SearchBar = () => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
    const navigate = useNavigate();
    const searchBarRef = useRef(null);
    
    // ============================================================================
    // REFS ДЛЯ ОПТИМІЗАЦІЇ ТА ЗАХИСТУ
    // ============================================================================
    
    const abortControllerRef = useRef(null); // Для скасування запитів
    const cacheRef = useRef(new Map()); // Кеш результатів: {query: {data, timestamp}}
    const debounceTimerRef = useRef(null); // Таймер для debounce

    // ============================================================================
    // ФУНКЦІЯ ОТРИМАННЯ ПІДКАЗОК З ЗАХИСТОМ
    // ============================================================================
    
    const fetchSuggestions = useCallback(async (searchQuery) => {
        // Перевірка мінімальної довжини
        if (searchQuery.length < MIN_QUERY_LENGTH) {
            setSuggestions([]);
            setIsSuggestionsVisible(false);
            return;
        }

        // Нормалізуємо запит (trim, lowercase)
        const normalizedQuery = searchQuery.trim().toLowerCase();
        
        // Перевірка кешу
        const cached = cacheRef.current.get(normalizedQuery);
        if (cached && (Date.now() - cached.timestamp < CACHE_EXPIRATION)) {
            console.log('📦 Використано кеш для:', normalizedQuery);
            setSuggestions(cached.data);
            setIsSuggestionsVisible(true);
            setLoading(false);
            return;
        }

        // Скасування попереднього запиту (якщо є)
        if (abortControllerRef.current) {
            console.log('🛑 Скасовано попередній запит');
            abortControllerRef.current.abort();
        }

        // Створення нового AbortController
        abortControllerRef.current = new AbortController();
        
        setLoading(true);
        setError(null);
        setIsSuggestionsVisible(true);

        try {
            console.log('🔍 Запит на сервер:', normalizedQuery);
            const response = await fetch(
                `${BACKEND_URL}/products/suggestions?query=${encodeURIComponent(searchQuery)}&limit=${SUGGESTIONS_LIMIT}`,
                { signal: abortControllerRef.current.signal }
            );
            
            if (!response.ok) {
                throw new Error(`HTTP помилка! Статус: ${response.status}`);
            }
            
            const data = await response.json();
            
            const limited = (data || []).slice(0, SUGGESTIONS_LIMIT);

            // Збереження в кеш
            cacheRef.current.set(normalizedQuery, {
                data: limited,
                timestamp: Date.now()
            });
            
            setSuggestions(limited);
            console.log('✅ Отримано результати:', data?.length || 0);
            
        } catch (err) {
            // Ігноруємо помилки скасованих запитів
            if (err.name === 'AbortError') {
                console.log('⏭️ Запит скасовано');
                return;
            }
            
            console.error("❌ Помилка завантаження підказок:", err);
            setError("Не вдалося завантажити підказки.");
            setSuggestions([]);
        } finally {
            setLoading(false);
            abortControllerRef.current = null;
        }
    }, []);

    // ============================================================================
    // DEBOUNCED ПОШУК
    // ============================================================================
    
    useEffect(() => {
        // Очищаємо попередній таймер
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Якщо запит порожній або коротший за мінімум - очищаємо
        if (query.length < MIN_QUERY_LENGTH) {
            setSuggestions([]);
            setIsSuggestionsVisible(false);
            return;
        }

        // Встановлюємо новий таймер
        debounceTimerRef.current = setTimeout(() => {
            fetchSuggestions(query);
        }, DEBOUNCE_DELAY);

        // Cleanup - очистка таймера при unmount або зміні query
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [query, fetchSuggestions]);

    // Обробник зміни в полі вводу
    const handleInputChange = (event) => {
        setQuery(event.target.value);
    };

    // Обробник кліку на підказку
    const handleSuggestionClick = (slugOrId) => {
        setQuery('');
        setSuggestions([]);
        setIsSuggestionsVisible(false);
        navigate(`/products/${slugOrId}`);
    };

    // Обробник кліку на кнопку пошуку
    const handleSearchClick = () => {
        const trimmed = query.trim();
        if (trimmed) {
            setSuggestions([]);
            setIsSuggestionsVisible(false);
            setQuery('');
            navigate(`/zapchastyny?q=${encodeURIComponent(trimmed)}`);
        }
    };

    // Обробник натискання Enter у полі вводу
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearchClick();
        }
    };

    // ============================================================================
    // CLEANUP ПРИ UNMOUNT
    // ============================================================================
    
    useEffect(() => {
        return () => {
            // Скасовуємо активні запити
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            // Очищаємо таймер debounce
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            // Очищаємо кеш (опціонально)
            cacheRef.current.clear();
        };
    }, []);

    // ============================================================================
    // ЗАКРИТТЯ ПІДКАЗОК ПРИ КЛІКУ ПОЗА КОМПОНЕНТОМ
    // ============================================================================
    
    useEffect(() => {
        function handleClickOutside(event) {
            if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
                setIsSuggestionsVisible(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [searchBarRef]);

    // Визначаємо чи показувати dropdown
    const showDropdown = isSuggestionsVisible && query.length >= 3;

    return (
        <div className="search-bar-container" ref={searchBarRef}>
            <div className={`search-bar ${showDropdown ? 'has-dropdown-open' : ''}`}>
                <input
                    type="text"
                    className="search-input"
                    placeholder="Назва деталі, марка або модель машини"
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => query.length >= 3 && setIsSuggestionsVisible(true)}
                    onKeyPress={handleKeyPress}
                />
                <button className="search-button" onClick={handleSearchClick}>
                    <FaSearch />
                </button>
            </div>
            {showDropdown && (
                <ul className="suggestions-list">
                    {loading && <li className="suggestion-item suggestion-loading">Завантаження...</li>}
                    {error && <li className="suggestion-item suggestion-error">{error}</li>}
                    {!loading && !error && suggestions.length === 0 && query.length >= 3 && (
                        <li className="suggestion-item suggestion-no-results">Нічого не знайдено</li>
                    )}
                    {!loading && !error && suggestions.map((suggestion) => (
                        <li
                            key={suggestion.id}
                            className="suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion.slug || suggestion.id)}
                            role="button"
                            tabIndex="0"
                            onKeyPress={(e) => e.key === 'Enter' && handleSuggestionClick(suggestion.slug || suggestion.id)}
                        >
                            {suggestion.name_ua}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default SearchBar;
